import axios from "axios";
import { ApiData } from "../utils/ApiData";
import { sanitizePayload } from "../utils/sanitize";
import { forceLogout } from "../utils/authUtils";

// Environment-aware logging utility (to avoid circular dependency with logger.js)
const isDevelopment = process.env.NODE_ENV === 'development';
const apiLog = {
  debug: (...args) => {
    if (isDevelopment) {
      console.debug('[API]', ...args);
    }
  },
  info: (...args) => {
    if (isDevelopment) {
      console.log('[API]', ...args);
    }
  },
  warn: (...args) => {
    console.warn('[API]', ...args);
  },
  error: (...args) => {
    console.error('[API]', ...args);
  }
};

// Use environment variable for baseURL (injected at build time)
let baseURL = process.env.REACT_APP_API_BASE_URL;

// If no environment variable is set, use environment-specific defaults
if (!baseURL) {
  const environment = process.env.NODE_ENV || "development";
  
  switch (environment) {
    case "production":
      baseURL = "https://homastu.com";
      break;
    case "kubernetes":
      baseURL = "https://staging.homastu.com";
      break;
    case "staging":
      baseURL = "http://staging.homastu.com";
      break;
    case "development":
    case "local":
    default:
      baseURL = "http://localhost:8089";
      break;
  }
}

// Log the baseURL for debugging
apiLog.info('API Service initialized', { baseURL, environment: process.env.NODE_ENV });

// CSRF token handling for session-based approach
let csrfToken = null;
let csrfRefreshInterval = null;

// --- Simple fixed-delay retry helper (generic) ---
const DEFAULT_RETRIES = 2; // total extra attempts (in addition to the initial)
const RETRY_DELAY_MS = 1000; // fixed delay between attempts

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isRetryableError(error) {
  // Network error or timeout
  if (!error.response) return true;
  const status = error.response.status;
  // Retry for common transient statuses
  return status === 502 || status === 503 || status === 504 || status === 429;
}

async function withRetry(fn, { retries = DEFAULT_RETRIES, delayMs = RETRY_DELAY_MS } = {}) {
  let attempt = 0;
  while (true) {
    try {
      return await fn();
    } catch (err) {
      if (attempt >= retries || !isRetryableError(err)) throw err;
      attempt += 1;
      await sleep(delayMs);
    }
  }
}

// Function to get CSRF token from backend
async function fetchCsrfToken() {
  try {
    // Backend has context-path=/api, construct endpoint properly
    // If baseURL already includes /api, don't add it again
    const needsApiPath = !baseURL.endsWith('/api');
    const endpoint = needsApiPath ? `${baseURL}/api/v1/security/csrf-token` : `${baseURL}/v1/security/csrf-token`;
    apiLog.debug('Fetching CSRF token from backend', { 
      endpoint: endpoint,
      baseURL: baseURL,
      endpointPath: '/v1/security/csrf-token',
      environment: process.env.NODE_ENV,
      timestamp: new Date().toISOString()
    });
    
    // Make a request to the CSRF token endpoint with session support
    const response = await axios.get(endpoint, {
      withCredentials: true, // Important for session handling
      timeout: 10000, // 10 second timeout
      validateStatus: function (status) {
        // Accept any status code to avoid throwing errors
        return true;
      }
    });
    
    // Enhanced response logging - response is wrapped in ApiData
    const rawResponse = response?.response || response;
    apiLog.debug('CSRF token response received', {
      status: rawResponse?.status,
      statusText: rawResponse?.statusText,
      headerCount: Object.keys(rawResponse?.headers || {}).length,
      hasData: !!rawResponse?.data,
      responseType: typeof response,
      responseUndefined: response === undefined,
      responseNull: response === null,
      hasHeaders: !!rawResponse?.headers
    });
    
    // Check if response is undefined or null
    if (!response) {
      apiLog.error('CSRF token response is undefined or null');
      return false;
    }
    
    // Check if the response status indicates success (use raw response for status)
    if (rawResponse?.status && (rawResponse.status < 200 || rawResponse.status >= 300)) {
      apiLog.error('CSRF token request failed with status', {
        status: rawResponse.status,
        statusText: rawResponse.statusText,
        data: rawResponse.data
      });
      return false;
    }
    
    // Get token from the response headers (backend sends X-CSRF-TOKEN)
    // Access headers from the raw axios response, not the ApiData wrapper
    const headerToken = rawResponse?.headers?.['x-csrf-token'] || rawResponse?.headers?.['X-CSRF-TOKEN'];
    if (headerToken) {
      csrfToken = headerToken;
      apiLog.debug('CSRF token successfully fetched from headers', { 
        tokenPrefix: headerToken.substring(0, 10) + '...',
        tokenLength: headerToken.length
      });
      return true;
    }
    
    // If no header token, log detailed debug info
    apiLog.warn('No CSRF token found in response headers', {
      availableHeaders: Object.keys(rawResponse?.headers || {}),
      hasData: !!rawResponse?.data,
      hasResponseData: !!rawResponse?.data
    });
    
    return false;
  } catch (error) {
    apiLog.error('Error fetching CSRF token', error, {
      errorType: typeof error,
      errorMessage: error?.message,
      errorCode: error?.code,
      errorStack: error?.stack?.substring(0, 500)
    });
    
    if (error.response) {
      apiLog.debug('CSRF fetch error response', {
        status: error.response.status,
        statusText: error.response.statusText,
        hasHeaders: !!error.response.headers,
        hasData: !!error.response.data
      });
    } else if (error.request) {
      apiLog.debug('CSRF fetch error - no response received', {
        hasRequest: !!error.request,
        timeout: error.timeout,
        code: error.code
      });
    }
    return false;
  }
}

// Initialize by fetching the CSRF token once (no periodic refresh)
(async function initializeCsrf() {
  const success = await fetchCsrfToken();
  if (success) {
    apiLog.debug('CSRF token initialized successfully');
  } else {
    apiLog.warn('Initial CSRF token fetch failed, will retry on first 403 error');
  }
})();

// Helper: detect invalid/expired JWT
function isAuthTokenInvalid(error) {
  if (!error?.response) return false;
  const res = error.response;
  const status = res.status;

  // Preferred: standardized backend response
  if (status === 401) {
    const hdr = (res.headers && (res.headers['www-authenticate'] || res.headers['WWW-Authenticate'])) || '';
    const code = res.data && (res.data.code || res.data.errorCode);
    const headerSaysInvalid = typeof hdr === 'string' && hdr.toLowerCase().includes('invalid_token');
    const codeSaysInvalid = code === 'AUTH_TOKEN_EXPIRED' || code === 'AUTH_TOKEN_INVALID';
    if (headerSaysInvalid || codeSaysInvalid) return true;
    // If backend says auth required (no token), trigger redirect logic elsewhere
    if (code === 'AUTH_REQUIRED') {
      const requiresAuth = !!(error && error.config && error.config.headers && error.config.headers['X-Requires-Auth']);
      if (requiresAuth && typeof window !== 'undefined' && window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
      // For non-auth-required requests, do not redirect; let the caller handle UI fallback
      return false;
    }
  }

  // Fallback for legacy/misclassified responses
  // Some backends may (incorrectly) return 500 for auth token errors
  const statusCouldBeAuth = status === 401 || status === 403 || status === 500;
  if (!statusCouldBeAuth) return false;
  let message = '';
  const data = res.data;
  if (data) {
    if (typeof data === 'string') message = data;
    else if (data.message) message = data.message;
    else if (data.error && data.error.message) message = data.error.message;
  }
  const msg = String(message).toLowerCase();
  return (
    msg.includes('jwt expired') ||
    msg.includes('expiredjwt') ||
    msg.includes('token expired') ||
    msg.includes('invalid token') ||
    msg.includes('invalid jwt') ||
    msg.includes('badcredentials') ||
    msg.includes('signature') || // generic JWT signature/invalid
    msg.includes('invalid token received')
  );
}

// Helper: detect CSRF-specific errors (not general authorization failures)
function isCSRFError(error) {
  if (!error?.response) return false;
  
  const status = error.response.status;
  const message = (error.response.data?.message || '').toLowerCase();
  const code = error.response.data?.code;
  
  // Only retry if it's specifically a CSRF token issue
  if (status === 403) {
    // Check for CSRF-specific indicators
    return (
      message.includes('csrf') ||
      message.includes('invalid token') ||
      message.includes('token expired') ||
      message.includes('token missing') ||
      code === 'CSRF_TOKEN_INVALID' ||
      code === 'CSRF_TOKEN_EXPIRED' ||
      code === 'CSRF_TOKEN_MISSING' ||
      // Spring Security default CSRF error message
      message.includes('could not verify the provided csrf token')
    );
  }
  
  return false;
}

// Global axios response interceptor - Industry standard: Always return ApiData
axios.interceptors.response.use(
  // Success response - wrap in ApiData
  response => {
    apiLog.debug('Axios interceptor - Success response', {
      status: response.status,
      url: response.config?.url,
      method: response.config?.method?.toUpperCase(),
      hasData: !!response.data
    });
    return new ApiData(response);
  },
  // Error response - standardize and wrap in ApiData
  async error => {
    const originalRequest = error.config || {};
    
    // Enhanced error logging
    apiLog.error('Axios interceptor - Error response', {
      message: error.message,
      code: error.code,
      url: originalRequest.url,
      method: originalRequest.method?.toUpperCase(),
      status: error.response?.status,
      statusText: error.response?.statusText,
      hasResponse: !!error.response,
      hasRequest: !!error.request,
      timeout: originalRequest.timeout,
      timestamp: new Date().toISOString()
    });

    // 1) If JWT is invalid/expired -> logout and redirect to login
    if (isAuthTokenInvalid(error)) {
      // Use centralized force logout from authUtils
      forceLogout('Invalid or expired token detected');
      
      // Redirect to login
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
      return Promise.reject(new ApiData(error.response || {
        status: 401,
        data: { status: 'error', message: 'Authentication failed', code: 'AUTH_FAILED' }
      }));
    }

    // 2) CSRF-specific error handling - Only retry if it's actually a CSRF issue
    if (error.response && !originalRequest._retry && isCSRFError(error)) {
      apiLog.debug('CSRF-specific error detected, attempting token refresh', {
        status: error.response.status,
        message: error.response.data?.message,
        code: error.response.data?.code
      });
      originalRequest._retry = true;
      
      try {
        const refreshSuccess = await fetchCsrfToken();
        if (refreshSuccess && csrfToken) {
          apiLog.debug('Retrying request with new CSRF token');
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers['X-CSRF-TOKEN'] = csrfToken;
          
          try {
            const retryResponse = await axios(originalRequest);
            apiLog.debug('Request retry successful after CSRF refresh');
            return new ApiData(retryResponse);
          } catch (retryError) {
            apiLog.error('Request retry failed even after CSRF refresh', retryError);
            return Promise.reject(new ApiData(retryError.response || {
              status: 403,
              data: { status: 'error', message: 'Access forbidden after CSRF retry', code: 'CSRF_RETRY_FAILED' }
            }));
          }
        } else {
          apiLog.error('Failed to refresh CSRF token for CSRF error');
          return Promise.reject(new ApiData(error.response || {
            status: 403,
            data: { status: 'error', message: 'CSRF token refresh failed', code: 'CSRF_REFRESH_FAILED' }
          }));
        }
      } catch (csrfRefreshError) {
        apiLog.error('Exception during CSRF token refresh', csrfRefreshError);
        return Promise.reject(new ApiData(error.response || {
          status: 403,
          data: { status: 'error', message: 'CSRF token refresh exception', code: 'CSRF_REFRESH_EXCEPTION' }
        }));
      }
    }

    // 3) Authorization errors (403) that are NOT CSRF-related should NOT be retried
    if (error.response && error.response.status === 403) {
      apiLog.debug('Authorization error (403) - not retrying for security');
      // Let it fall through to standard error handling
    }

    // 4) Standardize all other errors as ApiData
    if (error.response) {
      return Promise.reject(new ApiData(error.response));
    }
    
    // Network/setup errors
    return Promise.reject(new ApiData({
      status: 0,
      data: {
        status: 'error',
        message: error.message || 'Network error. Please check your connection.',
        code: 'NETWORK_ERROR'
      }
    }));
  }
);

async function get(apiPath, authRequired) {
  // Ensure apiPath is properly encoded
  const encodedPath = encodeURI(apiPath);
  const fullUrl = `${baseURL}${encodedPath}`;
  
  // Debug logging for API calls
  apiLog.debug('GET request initiated', {
    apiPath: apiPath,
    baseURL: baseURL,
    fullUrl: fullUrl,
    authRequired: authRequired,
    timestamp: new Date().toISOString()
  });
  
  const apiData = await withRetry(() =>
    axios.get(
      fullUrl,
      authRequired ? getAuthConfig() : {}
    )
  );
  
  apiLog.debug('GET response received', { 
    hasData: !!apiData,
    status: apiData?.getStatus?.(),
    success: apiData?.isSuccess?.()
  });
  return apiData; // Always returns ApiData from interceptor
}

// Helper to GET binary content (e.g., PDF) with optional auth
async function getBlob(apiPath, authRequired) {
  const encodedPath = encodeURI(apiPath);
  const cfg = authRequired ? getAuthConfig() : {};
  cfg.responseType = 'blob';
  
  const apiData = await withRetry(() =>
    axios.get(
      `${baseURL}${encodedPath}`,
      cfg
    )
  );
  
  return apiData; // Returns ApiData, caller can use apiData.getData() for Blob
}

async function post(apiPath, body, authRequired) {
  // Ensure apiPath is properly encoded
  const encodedPath = encodeURI(apiPath);
  const fullUrl = `${baseURL}${encodedPath}`;
  
  // Debug logging for API calls
  apiLog.debug('POST request initiated', {
    apiPath: apiPath,
    baseURL: baseURL,
    fullUrl: fullUrl,
    authRequired: authRequired,
    hasBody: !!body,
    bodyType: body?.constructor?.name || typeof body,
    timestamp: new Date().toISOString()
  });
  
  // For POST requests, always ensure we have a CSRF token
  if (!csrfToken) {
    apiLog.debug('No CSRF token available, fetching before POST request');
    await fetchCsrfToken();
  }
  
  // Detect if body is FormData
  const isFormData = body instanceof FormData;
  
  const config = authRequired ? getAuthConfig(true, isFormData) : { 
    withCredentials: true, 
    headers: { 
      'X-CSRF-TOKEN': csrfToken,
      ...(isFormData ? {} : { 'Content-Type': 'application/json' })
    } 
  };
  
  apiLog.debug('POST request config', {
    hasConfig: !!config,
    hasCsrfToken: !!csrfToken,
    isFormData: isFormData,
    withCredentials: config?.withCredentials
  });
  
  const safeBody = (body instanceof FormData) ? body : sanitizePayload(body);
  const apiData = await withRetry(() =>
    axios.post(
      fullUrl,
      safeBody,
      config
    )
  );
  
  apiLog.debug('POST response received', { 
    hasData: !!apiData,
    status: apiData?.getStatus?.(),
    success: apiData?.isSuccess?.()
  });
  return apiData; // Always returns ApiData from interceptor
}

async function put(apiPath, body, authRequired) {
  // Ensure apiPath is properly encoded
  const encodedPath = encodeURI(apiPath);
  
  // For PUT requests, always ensure we have a CSRF token
  if (!csrfToken) {
    apiLog.debug('No CSRF token available, fetching before PUT request');
    await fetchCsrfToken();
  }
  
  // Detect if body is FormData
  const isFormData = body instanceof FormData;
  
  const safeBody = isFormData ? body : sanitizePayload(body);
  const apiData = await withRetry(() =>
    axios.put(
      `${baseURL}${encodedPath}`,
      safeBody,
      authRequired ? getAuthConfig(true, isFormData) : { 
        withCredentials: true, 
        headers: { 
          'X-CSRF-TOKEN': csrfToken,
          ...(isFormData ? {} : { 'Content-Type': 'application/json' })
        } 
      }
    )
  );
  
  apiLog.debug('PUT response received', { hasData: !!apiData });
  return apiData; // Always returns ApiData from interceptor
}

async function del(apiPath, authRequired) {
  // Ensure apiPath is properly encoded
  const encodedPath = encodeURI(apiPath);
  
  // For DELETE requests, always ensure we have a CSRF token
  if (!csrfToken) {
    apiLog.debug('No CSRF token available, fetching before DELETE request');
    await fetchCsrfToken();
  }
  
  const apiData = await withRetry(() =>
    axios.delete(
      `${baseURL}${encodedPath}`,
      authRequired ? getAuthConfig(true) : { withCredentials: true, headers: { 'X-CSRF-TOKEN': csrfToken } }
    )
  );
  
  apiLog.debug('DELETE response received', { hasData: !!apiData });
  return apiData; // Always returns ApiData from interceptor
}

async function postWithBasicAuth(apiPath, authDetails, body = {}) {
  // Ensure apiPath is properly encoded
  const encodedPath = encodeURI(apiPath);
  
  // Ensure we have the latest CSRF token before login
  await fetchCsrfToken();
  
  const apiData = await axios.post(`${baseURL}${encodedPath}`, sanitizePayload(body), {
    headers: {
      Authorization:
        "Basic " +
        window.btoa(authDetails.email + ":" + authDetails.password),
      'X-CSRF-TOKEN': csrfToken,
    },
    withCredentials: true, // Important for CSRF cookie handling
  });

  apiLog.debug('Login response received', { hasData: !!apiData });
  
  // Extract token from raw response headers (ApiData wraps the original response)
  const rawResponse = apiData.getRawData();
  const authHeader = rawResponse.headers?.authorization || 
                    rawResponse.headers?.Authorization;
  
  apiLog.debug('Authorization header received', { hasHeader: !!authHeader });
  
  if (authHeader) {
    // Store the token directly since backend now sends it without Bearer prefix
    localStorage.setItem("token", authHeader);
    apiLog.debug('Token saved to localStorage');
    
    // Dispatch storage event to notify other components
    window.dispatchEvent(new Event('storage'));
  } else {
    apiLog.warn('No token found in response headers');
  }

  return { apiData, token: authHeader }; // Return ApiData and token
}

function getAuthConfig(requireCsrf = false, isFormData = false) {
  const token = localStorage.getItem("token");
  
  // Only add Bearer prefix if token exists
  const authHeader = token ? `Bearer ${token}` : null;
  
  const config = {
    headers: {},
    withCredentials: true, // Important for CSRF cookie handling
  };
  
  // Only set Content-Type for non-FormData requests
  // FormData requests need the browser to set Content-Type with boundary
  if (!isFormData) {
    config.headers['Content-Type'] = 'application/json';
  }
  
  // Only add Authorization header if we have a token
  if (authHeader) {
    config.headers.Authorization = authHeader;
  }
  
  // Add CSRF token if available
  if (csrfToken) {
    config.headers['X-CSRF-TOKEN'] = csrfToken;
  } else if (requireCsrf) {
    // If CSRF token is required but not available, log a warning
    apiLog.warn('CSRF token required but not available');
  }
  
  // Mark this request as requiring authentication so interceptors can decide redirect behavior
  config.headers['X-Requires-Auth'] = 'true';

  return config;
}

const apiService = {
  get,
  getBlob,
  post,
  put,
  delete: del,
  postWithBasicAuth,
  getBaseUrl: () => baseURL,
  fetchCsrfToken, // Export the function to refresh token when needed
};

export { baseURL };
export default apiService;
